import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "const/axios";
import { API_URL } from "const/constants";

const BASE = "/mara-agents";
const publicApiBase = (API_URL || "").replace(/\/api\/v1\/?$/, "");
const publicBase = (publicToken) => `${publicApiBase}/api/public/mara-signature/${publicToken}`;

export const useMaraAgents = (scope, options = {}) => {
  return useQuery({
    queryKey: ["maraAgents", scope || "all"],
    queryFn: async () => (await axios.get(BASE, { params: scope ? { scope } : undefined })).data,
    ...options,
  });
};

export const useCreateMaraAgent = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await axios.post(BASE, payload)).data,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["maraAgents"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useUpdateMaraAgent = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => (await axios.put(`${BASE}/${id}`, payload)).data,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["maraAgents"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useDeleteMaraAgent = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => axios.delete(`${BASE}/${id}`),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["maraAgents"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useUploadMaraSignature = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${BASE}/${id}/signature/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["maraAgents"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useSetMaraSignatureFromCanvas = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, imageBase64 }) =>
      (await axios.post(`${BASE}/${id}/signature/canvas`, { imageBase64 })).data,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["maraAgents"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useDeleteMaraSignature = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await axios.delete(`${BASE}/${id}/signature`)).data,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ["maraAgents"] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useSendMaraSignatureLink = (options = {}) => {
  return useMutation({
    mutationFn: async (id) => (await axios.post(`${BASE}/${id}/signature/send-link`)).data,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

export const getPublicMaraSignatureInfo = async (publicToken) => {
  const response = await axios.get(publicBase(publicToken));
  return response.data;
};

export const completePublicMaraSignature = async ({ publicToken, imageBase64 }) => {
  const response = await axios.post(`${publicBase(publicToken)}/complete`, { imageBase64 });
  return response.data;
};
